import type { CitationView } from "@/lib/ai/types";
import type { AnswerTraceView } from "@/lib/ui/chat-trace";

export const CHAT_HISTORY_LIMIT = 30;
export const CONVERSATION_TITLE_MAX = 80;

export const chatHistoryCopy = {
  nav: "Чаты",
  newChat: "Новый чат",
  inaccessible: "Документ больше недоступен",
  delete: "Удалить чат",
  deleteTitle: "Удалить чат?",
  deleteConfirm: "Удалить",
  deleteCancel: "Отмена",
} as const;

export function chatDeleteMessage(title: string): string {
  return `«${title}» будет удалён без восстановления.`;
}

export const CHAT_DELETE_POPOVER_WIDTH = 240;
export const CHAT_DELETE_POPOVER_GAP = 8;
export const CHAT_DELETE_CARET = 7;

export function chatDeletePopoverStyle(params: {
  trigger: { left: number; right: number; top: number; bottom: number };
  viewportWidth: number;
  viewportHeight: number;
}): {
  top: number;
  left: number;
  caretTop: number;
  caretSide: "left" | "right";
} {
  const width = CHAT_DELETE_POPOVER_WIDTH;
  const gap = CHAT_DELETE_POPOVER_GAP;
  const estimatedHeight = 144;
  let caretSide: "left" | "right" = "left";
  let left = params.trigger.right + gap;
  if (left + width > params.viewportWidth - gap) {
    left = params.trigger.left - gap - width;
    caretSide = "right";
  }
  left = Math.max(gap, left);
  let top = params.trigger.top;
  if (top + estimatedHeight > params.viewportHeight - gap) {
    top = Math.max(gap, params.viewportHeight - estimatedHeight - gap);
  }
  const triggerCenter = (params.trigger.top + params.trigger.bottom) / 2;
  const caretTop = Math.round(
    Math.min(
      estimatedHeight - CHAT_DELETE_CARET * 2 - 8,
      Math.max(8, triggerCenter - top - CHAT_DELETE_CARET),
    ),
  );
  return { top, left, caretTop, caretSide };
}

export type ChatTurnView = {
  id: string;
  question: string;
  answer: string;
  citations: CitationView[];
  sourceCount: number;
  status: "answered" | "no_sources" | "pending" | "failed" | "cancelled";
  trace: AnswerTraceView | null;
  createdAt: Date;
};

export type ChatHistoryItem = {
  id: string;
  title: string;
  when: string;
  folderId: string | null;
};

export type ChatFolderHistoryItem = {
  id: string;
  name: string;
};

export function newChatHref(): string {
  return "/chat";
}

export function conversationHref(id: string): string {
  return `/chat/${id}`;
}

export function isNewChatActive(pathname: string): boolean {
  return pathname === newChatHref();
}

export function isChatNavActive(pathname: string): boolean {
  return pathname === "/chat" || pathname.startsWith("/chat/");
}

export function isConversationActive(pathname: string, id: string): boolean {
  return pathname === conversationHref(id);
}

export function conversationTitle(question: string): string {
  const text = question.trim().replace(/\s+/g, " ");
  if (text.length <= CONVERSATION_TITLE_MAX) {
    return text;
  }
  return `${text.slice(0, CONVERSATION_TITLE_MAX - 1).trimEnd()}…`;
}

export function parseKeepTurns(raw: string): number | null {
  if (!raw.trim()) {
    return null;
  }
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 0 || value > 500) {
    return null;
  }
  return value;
}
